import React, { useState, useEffect } from 'react';

// Comps
import { TextField } from "@src/stories/elements/TextField/TextField";
import Select from '@src/stories/elements/Select/Select';

import {Checkbox} from '@src/stories/elements/Checkbox/Checkbox';
import ButtonSpinner from '@src/stories/elements/ButtonSpinner/ButtonSpinner';
import {Button} from '@arctheme-components/elements/Button/Button';
import {Link} from '@arctheme-components/elements/Link/Link';
import {Paragraph} from '@arctheme-components/elements/Paragraph/Paragraph';

const AddressForm = ({
  address,
  countries,
  provinces,
  newForm,
  shopifyCustomerId,
  getProvinces,
  onClickCancel,
  updateCustomerAddress,
  updatingAddress,
  updateError,
  updateSuccess,
  addCustomerAddress,
  addingAddress,
  addError,
  addSuccess,
  locales,
  resetForm,
}: {
  address: any;
  countries: any;
  provinces: any;
  newForm: boolean;
  shopifyCustomerId: number;
  getProvinces: (countryCode: string) => void;
  onClickCancel: (event: React.ClickEvent) => void;
  updateCustomerAddress: (customerId: number, addressId: number, addressData: any) => Promise<{ success: boolean }>;
  updatingAddress: boolean;
  updateError: string;
  updateSuccess: boolean;
  addCustomerAddress: (customerId: number, addressData: any) => Promise<{ success: boolean }>;
  addingAddress: boolean;
  addError: string;
  addSuccess: boolean;
  locales: any;
  resetForm: any;
}) => {
  const { firstName, lastName, company, address1, address2, city, province, zip, country, provinceCode } = address;

  // States
  const initialDependenciesState = {
    firstName,
    lastName,
    company,
    address1,
    address2,
    city,
    province,
    zip,
    country
  };
  const [dependenciesState, setDependenciesState]: any = useState(initialDependenciesState);
  const [isDefaultAddress, setIsDefaultAddress] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  // Handle input change and update global dependency state
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;

    // For checkboxes, use the checked value, otherwise use the input value
    const newValue = inputType === 'checkbox' ? checked : value;

    // Update the global dependency state
    updateDependencyState(name, newValue);
  };

  // Function to update the global dependencies state
  const updateDependencyState = (name, value) => {
    const newData = {
      ...dependenciesState,
      [name]: value
    };

    setDependenciesState(newData);
    toggleIsUpdated(newData);
  };

  const toggleIsUpdated = (newData) => {
    const updated = Object.keys(newData).some((key) => address[key] !== newData[key]);

    setIsUpdated(updated);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const provinceCode = dependenciesState['province'];
    const countryName = dependenciesState['country'];
    const data = {
      ...dependenciesState,
      country: countries.find((country) => country.name === countryName).code,
      province: provinceCode && provinces ? provinces.find((province) => province.code === provinceCode).name : '',
      default: isDefaultAddress
    };

    let isSuccess = false;

    if (!newForm) {
      const { success } = await updateCustomerAddress(shopifyCustomerId, address.id, data);
      isSuccess = success;
    } else {
      const { success } = await addCustomerAddress(shopifyCustomerId, data);
      isSuccess = success;
    }

    if (isSuccess) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    resetForm();
  };

  // Get provinces based on selected country
  useEffect(() => {
    const countryData = countries.find((country) => country.name === dependenciesState.country);

    if (countryData) {
      getProvinces(countryData.code);
    }
  }, [dependenciesState]);

  useEffect(() => {
    if (!provinces) return;

    if (!provinces.length) {
      setDependenciesState((prev) => ({
        ...prev,
        province: '',
      }));
    } else {
      const currentProvince = dependenciesState['province'];

      if (!currentProvince) {
        setDependenciesState((prev) => ({
          ...prev,
          province: provinces[0].code
        }));
      } else {
        setDependenciesState((prev) => ({
          ...prev,
          province: provinceCode
        }));
      }
    }
  }, [provinces]);

  const countryOptions = countries.map((country) => ({
    name: country.name,
    value: country.name
  }));
  const provinceOptions = provinces?.map((province) => ({
    name: province.name,
    value: province.code
  }));

  return (
    <>


          <Link
            link={' '}
            classes={`flex justify-start items-center gap-x-4 mb-8`}
            onClick={(e) => onClickCancel(e)}
          >
            <i className='icon-arrow-back text-2xl'></i>
            <span className={`h6`}>Back to address book</span>
          </Link>

          <form
            id='addressForm'
            className="md:min-w-[400px]"
            onSubmit={handleSubmit}
          >
            <div className='flex flex-col gap-y-8 items-center'>
              {/* First Name */}
              <TextField
                inputType='text'
                type="floating-input"
                id='firstNameAddress'
                className='w-full'
                name='firstName'
                value={dependenciesState['firstName']}
                onChange={handleInputChange}
                label={locales.firstName}
                placeholder={locales.firstName}
              />

              {/* Last Name */}
              <TextField
                inputType='text'
                type="floating-input"
                id='lastNameAddress'
                className='w-full'
                name='lastName'
                value={dependenciesState['lastName']}
                onChange={handleInputChange}
                label={locales.lastName}
                placeholder={locales.lastName}
              />

              {/* Address1 */}
              <TextField
                inputType='text'
                type="floating-input"
                id='address1Address'
                name='address1'
                className='w-full'
                value={dependenciesState['address1']}
                onChange={handleInputChange}
                label={locales.address1}
                placeholder={locales.address1}
              />

              {/* Address2 */}
              <TextField
                inputType='text'
                type="floating-input"
                id='address2Address'
                name='address2'
                className='w-full'
                value={dependenciesState['address2']}
                onChange={handleInputChange}
                label={locales.address2}
                placeholder={locales.address2}
              />

              {/* State */}
              {provinceOptions && provinceOptions.length > 0 && (
                <Select
                  id='province'
                  onChange={handleInputChange}
                  options={provinceOptions}
                  label={locales.province}
                  placeholder={'Select'}
                  selected={dependenciesState['province']}
                  returnEvent={true}
                />
              )}

              {/* Zip */}
              <TextField
                inputType='text'
                type="floating-input"
                id='zipAddress'
                className='w-full'
                name='zip'
                value={dependenciesState['zip']}
                onChange={handleInputChange}
                label={locales.zip}
                placeholder={locales.zip}
              />

              {/* Country */}
              <Select
                id='country'
                onChange={handleInputChange}
                options={countryOptions}
                label={locales.country}
                placeholder={'Select'}
                selected={dependenciesState['country']}
                returnEvent={true}
              />

              <Checkbox
                id='defaultAddress'
                classes={`w-full b4`}
                label={locales.setAsDefault}
                checked={isDefaultAddress}
                onChange={() => setIsDefaultAddress((prev) => !prev)}
              />

              {(updateError || addError) && (
                <Paragraph
                  type='errors text-center'
                  text={updateError || addError}
                />
              )}

              <div className='grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-4 w-full '>
                <ButtonSpinner
                  isLoading={updatingAddress || addingAddress}
                  classes={`button button-primary w-full !mt-0`}
                  ariaLabel='Submit address form'
                  disabled={!isUpdated && !isDefaultAddress}
                  loadingText={'Saving'}
                >
                  {newForm ? `Create Address` : `Save`}
                </ButtonSpinner>

                <Link
                  link={' '}
                  classes='button button-secondary'
                  onClick={(e) => onClickCancel(e)}
                >
                  <span>Cancel</span>
                </Link>

              </div>
            </div>
          </form>

    </>
  );
};

export default AddressForm;
